import { useCallback, useEffect, useRef, useState } from 'react'
import { apiGet, apiPost } from '../../generic/Api_Functions'

const NOT_CHOSEN = { known: false, show: false }

/**
 * One on/off preference of the details table kept in Sun per user, in `user/tabletune` under its own key with ONE
 * field (contract §6). What the user chose wins; with nothing saved, or when reading it fails, it is `defaultValue`
 * (and the default is never written: only a press saves). Reading never retries nor warns, and a failed save keeps
 * the choice for the session without a notice (POL4).
 *
 * @param {Object} options
 * @param {String} options.tableKey The frozen key of the preference
 * @param {String} options.fieldName The frozen name of its only field
 * @param {Boolean} options.defaultValue What it is when the user has not chosen
 * @param {Boolean} options.enabled Reads it (false: a plan with nothing to show it on asks nothing)
 * @returns {[Boolean, Function]} The value and the function that flips it
 */
const useTableTuneFlag = ({ tableKey, fieldName, defaultValue, enabled = true }) => {
  const [chosen, setChosen] = useState(NOT_CHOSEN)
  const choseRef = useRef(false)
  // The ids Sun gave to the saved row: the first save goes without them (contract §6.3).
  // `null` is the API boundary: Sun reads `"id": null` as "new".
  const idsRef = useRef({ id: null, fieldId: null })
  const savingRef = useRef(Promise.resolve())
  const valueRef = useRef(defaultValue)

  useEffect(() => {
    if (!enabled) return undefined
    let current = true
    const read = async () => {
      let response = {}
      try {
        response = await apiGet('user/tabletune/' + tableKey)
      } catch (error) {
        response = {}
      }
      if (!current) return
      const fields = Array.isArray(response?.fields) ? response.fields : []
      const field = fields.find((item) => item?.name === fieldName)
      if (field === undefined) return
      idsRef.current = { id: response.id ?? null, fieldId: field.id ?? null }
      if (typeof field.show === 'boolean' && !choseRef.current) setChosen({ known: true, show: field.show })
    }
    read()
    return () => { current = false }
  }, [enabled, tableKey, fieldName])

  const value = chosen.known ? chosen.show : defaultValue
  valueRef.current = value

  const toggle = useCallback(() => {
    const next = !valueRef.current
    valueRef.current = next
    choseRef.current = true
    setChosen({ known: true, show: next })
    // One save after the other, so the second one carries the ids the first one got.
    savingRef.current = savingRef.current.then(async () => {
      try {
        const response = await apiPost('user/tabletune', {
          id: idsRef.current.id,
          tableKey,
          fields: [{ id: idsRef.current.fieldId, name: fieldName, show: next, order: 1 }]
        })
        const saved = Array.isArray(response?.fields) ? response.fields.find((item) => item?.name === fieldName) : {}
        idsRef.current = { id: response?.id ?? idsRef.current.id, fieldId: saved?.id ?? idsRef.current.fieldId }
      } catch (error) {
        // POL4: the choice stays for the session and nobody is bothered.
      }
    })
  }, [tableKey, fieldName])

  return [value, toggle]
}

export default useTableTuneFlag
